import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

const VISITOR_ID_KEY = 'noq5_vid';
const SESSION_ID_KEY = 'noq5_sid';

function generateId(): string {
  return crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function getOrCreateVisitorId(): { visitorId: string; isReturning: boolean } {
  let visitorId = localStorage.getItem(VISITOR_ID_KEY);
  const isReturning = !!visitorId;
  if (!visitorId) {
    visitorId = generateId();
    localStorage.setItem(VISITOR_ID_KEY, visitorId);
  }
  return { visitorId, isReturning };
}

function getOrCreateSessionId(): string {
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = generateId();
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
}

function getUTMParams(): Record<string, string | null> {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_term: params.get('utm_term'),
    utm_content: params.get('utm_content'),
  };
}

function getDeviceType(): string {
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

function getBrowser(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  return 'Other';
}

function getOS(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Win')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  return 'Other';
}

export function useVisitorTracking() {
  const location = useLocation();
  const pageLoadTime = useRef(Date.now());

  useEffect(() => {
    pageLoadTime.current = Date.now();

    const trackVisit = async () => {
      try {
        const { visitorId, isReturning } = getOrCreateVisitorId();
        const sessionId = getOrCreateSessionId();
        const utm = getUTMParams();

        await supabase.from('website_visits').insert({
          visitor_id: visitorId,
          session_id: sessionId,
          page_path: location.pathname,
          page_title: document.title,
          referrer: document.referrer || null,
          utm_source: utm.utm_source,
          utm_medium: utm.utm_medium,
          utm_campaign: utm.utm_campaign,
          utm_term: utm.utm_term,
          utm_content: utm.utm_content,
          device_type: getDeviceType(),
          browser: getBrowser(),
          os: getOS(),
          screen_resolution: `${screen.width}x${screen.height}`,
          language: navigator.language,
          is_returning: isReturning,
        });
      } catch (e) {
        // Silent fail - don't break the site for tracking
      }
    };

    // Small delay to not block rendering
    const timer = setTimeout(trackVisit, 500);
    return () => clearTimeout(timer);
  }, [location.pathname]);
}
